import { gql } from '@apollo/client';
import { StreakWeeklySession } from '@fenderdigital/api-definitions/play';
import AuthorizationHeader from './authorization-header';
import fetch from '../core/fetch';
import { parseResponse } from '../core/fetch/response';
import { datesAreOnSameDay, getDateNDaysAway } from '../utils/date-utils';
import STREAKS_RESULTS_QUERY from '../queries/streaks';

// d.getTimezoneOffset() minutes to rfc3339 format, e.g. 420 becomes -07:00
export const toRFC3339TimezoneOffset = (offset: number): string => {
  const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);
  if (offset === 0) {
    return '+00:00';
  }
  const sign = offset > 0 ? '-' : '+';
  offset = Math.abs(offset);
  return `${sign}${pad(Math.floor(offset / 60))}:${pad(offset % 60)}`;
};

// changes a Date into a rfc3339 timestamp, e.g. 2019-10-30T15:30:11-07:00
export const toRFC3339 = (d: Date): any => {
  const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}${toRFC3339TimezoneOffset(
    d.getTimezoneOffset(),
  )}`;
};

export const create = async (sessionId: string): Promise<StreakWeeklySession> => {
  const date = toRFC3339(new Date());
  const headers = await AuthorizationHeader.getCognitoHeader();
  const url = `${process.env.REACT_APP_CLIENT_LIBRARY_API}/streaks/weekly-session`;
  const params = {
    method: 'POST',
    ...headers,
    body: JSON.stringify({
      'offset-date-time': date,
      'session-id': sessionId,
    }),
  };
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  const response = await fetch(url, params);
  return parseResponse(response);
};

const WEEKLY_SESSION_QUERY = gql`
  query weeklySession($offset: GetStreakWeeklySessionInput!) {
    streakWeeklySession(input: $offset) {
      milestones
      milestoneGoal
      milestoneCompletedToday
      daysRemaining
    }
  }
`;

export const get = async () => {
  const date = toRFC3339(new Date());
  try {
    const getClient = await import(/* webpackChunkName: "apollo" */ '../core/apollo/playqlClient');
    const client = await getClient.default();
    const resp = await client?.query({
      query: WEEKLY_SESSION_QUERY,
      variables: {
        offset: { offsetDateTime: date },
      },
    });
    const respData = resp?.data.streakWeeklySession;
    return {
      milestones: respData.milestones,
      'days-remaining': respData.daysRemaining,
      'milestone-completed-today': respData.milestoneCompletedToday,
      'milestone-goal': respData.milestoneGoal,
    };
  } catch (e) {
    return console.error(e);
  }
};

export const remove = async (): Promise<Response> => {
  const headers = await AuthorizationHeader.getCognitoHeader();
  const url = `${process.env.REACT_APP_CLIENT_LIBRARY_API}/streaks`;
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  const response = await fetch(url, {
    method: 'DELETE',
    ...headers,
  });
  return parseResponse(response);
};

// ********************
// Streaks V2 Functions
// ********************

export interface StreaksResponse {
  currentCount: number;
  currentStarted: string;
  lastUpdated: string;
  longestCount: number;
  longestStarted: string;
  longestEnded: string;
}

interface Streak {
  currentStreak: number;
  retry: boolean;
}

/**
 * Gets the a user's current streaks status
 * @return {Object} Returns an object with the user's streak data.
 */
export const fetchStreaks = async (): Promise<Streak | void> => {
  try {
    const getClient = await import(/* webpackChunkName: "apollo" */ '../core/apollo/fdsClient');
    const client = await getClient.default();
    const options = {
      query: STREAKS_RESULTS_QUERY,
    };
    const response = await client?.query(options);
    const data: StreaksResponse = response?.data.play_getStreak;

    return getCurrentStreak(data);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      throw error;
    }
    console.error(error);
  }
};

export const getCurrentStreak = (data: StreaksResponse): Streak => {
  const currentStreak = data?.currentCount;
  const today = new Date();
  const lastUpdatedDate = new Date(data?.lastUpdated);

  if (datesAreOnSameDay(today, lastUpdatedDate)) {
    return {
      currentStreak,
      retry: false,
    };
  }
  // If the last update date is not today, we'll need to retry the fetch
  return {
    currentStreak: predictStreak({ currentStreak, today, lastUpdatedDate }),
    retry: true,
  };
};

const predictStreak = ({
  currentStreak,
  today,
  lastUpdatedDate,
}: {
  currentStreak: number;
  today: Date;
  lastUpdatedDate: Date;
}): number => {
  if (lastUpdatedDate < getDateNDaysAway(today, -7)) {
    return 1;
  }
  return currentStreak + 1;
};
